import React, { useState, useEffect } from 'react'
import {
  DateShift,
  MentorRequestForEvent,
} from 'types/mentorRequest/mentorRequest'
import { TimePicker, Form, Input, FormInstance, Button } from 'antd'
import type { Moment } from 'moment'
import moment from 'moment'
import { useUpdateMentorRequestForEvent } from 'api/Event/MentorRequest'
import { myStore } from 'state/store'
import AddShiftModal from '../../Modals/AddShiftModal'
import { convertMinutesToLocalTime } from 'helper/utility'

interface ShiftsProps {
  form: FormInstance
  prevData: MentorRequestForEvent
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
}

const Shifts: React.FC<ShiftsProps> = ({ prevData, form, setCurrentStep }) => {
  const [setupTimeMinutes, setSetupTimeMinutes] = useState<number>(
    prevData.setUpDuration || 0,
  )
  const [cleanupTimeMinutes, setCleanupTimeMinutes] = useState<number>(
    prevData.cleanUpDuration || 0,
  )
  const [openAddShift, setOpenAddShift] = useState<boolean>(false)
  const [mentorShifts, setMentorShifts] = useState<DateShift[]>(
    prevData ? prevData.requiredShifts : [],
  )
  const { mutate: updateMentorRequestForEventMutation } =
    useUpdateMentorRequestForEvent()
  const setModalBtnLoader = myStore(state => state.setModalBtnLoader)

  const onSetupTimeChange = (time: Moment | null) => {
    if (time) {
      const hours = time.hour()
      const minutes = time.minute()
      const totalMinutes = hours * 60 + minutes
      setSetupTimeMinutes(totalMinutes)
    }
  }

  const onCleanupTimeChange = (time: Moment | null) => {
    if (time) {
      const hours = time.hour()
      const minutes = time.minute()
      const totalMinutes = hours * 60 + minutes
      setCleanupTimeMinutes(totalMinutes)
    }
  }

  const formSchema = {
    numberOfMentors: prevData.mentorsRequired,
    setupTime: prevData.setUpDuration
      ? moment()
          .hours(Math.floor(prevData.setUpDuration / 60))
          .minutes(prevData.setUpDuration % 60)
      : null,
    cleanUpTime: prevData.cleanUpDuration
      ? moment()
          .hours(Math.floor(prevData.cleanUpDuration / 60))
          .minutes(prevData.cleanUpDuration % 60)
      : null,
  }

  const handleFinish = (values: any) => {
    setModalBtnLoader(true)
    const payload = {
      mentorsRequired: parseInt(values.numberOfMentors),
      setUpDuration: setupTimeMinutes,
      cleanUpDuration: cleanupTimeMinutes,
      requiredShifts: mentorShifts,
    }
    updateMentorRequestForEventMutation(
      {
        payload,
        requestId: prevData._id,
      },
      {
        onSuccess: () => {
          setModalBtnLoader(false)
          setCurrentStep(2)
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      },
    )
  }

  return (
    <>
      <div className="mt-20px">
        <h3 className="text-16 w-600">How much time is needed for</h3>
        <Form
          form={form}
          initialValues={formSchema}
          layout="vertical"
          onFinish={handleFinish}
        >
          <div className="flex justify-between align-center mt-15px">
            <p className="m-0">Set up</p>
            <Form.Item name="setupTime">
              <TimePicker
                format="HH:mm"
                placeholder="Select time"
                onChange={onSetupTimeChange}
                className="w-150px h-50px"
                showNow={false}
                clearIcon={false}
                use12Hours={false}
              />
            </Form.Item>
          </div>
          <div className="flex justify-between align-center mt-15px">
            <p className="m-0">Clean up</p>
            <Form.Item name="cleanUpTime">
              <TimePicker
                format="HH:mm"
                placeholder="Select time"
                onChange={onCleanupTimeChange}
                className="w-150px h-50px"
                showNow={false}
                clearIcon={false}
                use12Hours={false}
              />
            </Form.Item>
          </div>

          <h3 className="text-16 w-600 mt-20px">Shift requirements</h3>
          <div className="flex justify-between align-center mt-15px">
            <p className="m-0">Number of mentor required</p>
            <div className="w-150px">
              <Form.Item name="numberOfMentors">
                <Input
                  name="numberOfMentors"
                  placeholder="0"
                  className=" h-50px"
                  type="number"
                  onWheel={e => e.currentTarget.blur()}
                />
              </Form.Item>
            </div>
          </div>
        </Form>

        {/* Show added shifts summary */}
        {mentorShifts.length > 0 && (
          <div className="mt-20px">
            {mentorShifts
              .sort(
                (a, b) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime(),
              )
              .map((dateShift, idx) => (
                <div
                  key={dateShift.date}
                  className="flex justify-between gap-10px mb-10px"
                >
                  <div className="text-14 w-400 mb-2px">
                    {moment(dateShift.date).format('dddd')}
                    <br />
                    <span className="text-12 w-400 text-gray">
                      {moment(dateShift.date).format('MMM D, YYYY')}
                    </span>
                  </div>
                  <div>
                    {dateShift.shifts.map((shift, sIdx) => (
                      <div
                        key={sIdx}
                        className="flex align-center justify-between gap-10px mb-10px"
                      >
                        <div
                          className="bg-grey px-20px py-15px radius-10 text-14"
                          style={{
                            minWidth: 180,
                            display: 'inline-block',
                            marginRight: '130px',
                          }}
                        >
                          {`${convertMinutesToLocalTime(
                            shift.start,
                            'h:mm A',
                          )} - ${convertMinutesToLocalTime(
                            shift.end,
                            'h:mm A',
                          )}`}
                        </div>
                        <div className="w-150px">
                          <Input
                            value={shift.mentorsRequired}
                            disabled
                            className="h-50px text-center"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}

        <div className="flex justify-center mt-20px mx-auto">
          <button
            className="prim-btn w-50p"
            onClick={() => setOpenAddShift(true)}
          >
            Add Shift
          </button>
        </div>
      </div>

      {openAddShift && (
        <AddShiftModal
          openModal={openAddShift}
          setOpenModal={setOpenAddShift}
          mentorShifts={mentorShifts}
          setMentorShifts={setMentorShifts}
        />
      )}
    </>
  )
}

export default Shifts
